import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchPatients } from "../../redux/slices/patientSlice";
import { fetchDoctors } from "../../redux/slices/doctorSlice";
import { fetchProcedures } from "../../redux/slices/procedureSlice";
import {
  createAppointmentThunk,
  updateAppointmentThunk,
  updateAppointmentDetailsThunk,
} from "../../redux/slices/appointmentSlice";
import { DEFAULT_DURATION } from "../../api/appointmentService";
import PatientModal from "../patients/PatientModal";
import { getApiErrorMessage } from "../../utils/apiError";
import { withDrTitle } from "../../utils/doctorName";
import { sameId } from "../../utils/ids";

const STATUS_OPTIONS = [
  { value: "Booked", label: "Booked" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "NoShow", label: "No Show" },
];

const STATUS_BADGE = {
  Booked: "primary",
  Completed: "success",
  Cancelled: "danger",
  NoShow: "warning",
};

const DURATIONS = [10, 15, 20, 30, 45, 60, 90, 120];

const EMPTY = {
  appointmentDate: "",
  fromTime: "",
  duration: DEFAULT_DURATION,
  patientId: "",
  patientName: "",
  doctorId: "",
  procedureId: "",
  status: "Booked",
  remarks: "",
};

const pad = (n) => String(n).padStart(2, "0");

const addMinutes = (time, mins) => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + mins;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
};

const minutesBetween = (from, to) => {
  if (!from || !to) return 0;
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  return th * 60 + tm - (fh * 60 + fm);
};

// API times come back as "HH:mm:ss" (possibly with fractions) — the time input wants "HH:mm".
const hhmm = (t) => (t || "").split(".")[0].slice(0, 5);

const fmt12 = (t) => {
  if (!t) return "—";
  const parts = t.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${pad(m)} ${ampm}`;
};

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

// Booking form state from a saved appointment, for status updates and editing.
const fromRecord = (rec) => {
  const fromTime = hhmm(rec.fromTime);
  const length = minutesBetween(fromTime, hhmm(rec.toTime));
  return {
    ...EMPTY,
    appointmentDate: String(rec.appointmentDate || "").split("T")[0],
    fromTime,
    duration: length > 0 ? length : DEFAULT_DURATION,
    patientId: rec.patientID || rec.patientId || "",
    patientName: rec.patientName || "",
    doctorId: rec.doctorID || rec.doctorId || "",
    procedureId: rec.procedureID || rec.procedureId || "",
    status: rec.status || "Booked",
    remarks: rec.remarks || "",
  };
};

const buildWhatsAppURL = (mobile, message) => {
  const clean = (mobile || "").replace(/\D/g, "");
  if (!clean) return null;
  const number = clean.startsWith("91") ? clean : `91${clean}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
};

const AppointmentModal = ({ show, onClose, editRecord, initialDate, initialTime }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patients = useSelector((s) => s.patients.list);
  const doctors = useSelector((s) => s.doctors.list);
  const procedures = useSelector((s) => s.procedures.list);

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);

  const isEdit = !!(editRecord?.appointmentID || editRecord?.id);
  const editId = editRecord?.appointmentID || editRecord?.id;
  // Only upcoming bookings can be rescheduled; a completed visit may already be invoiced.
  const canEditDetails = isEdit && (editRecord?.status || "Booked") === "Booked";

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || p.mobile || ""} (OP: ${p.opNumber || "—"})`,
    patientName: p.patientName,
    mobile: p.mobileNumber || p.mobile || "",
  }));

  const doctorOptions = doctors.map((d) => ({
    value: d.doctorID || d.id,
    label: d.doctorName,
    availability: d.availabilitySchedule || "",
  }));

  const procedureOptions = procedures.map((p) => ({
    value: p.procedureID || p.id,
    label: p.procedureName,
  }));

  // An existing booking can have a length that isn't in the list — keep it selectable.
  const durationOptions = [...new Set([...DURATIONS, Number(form.duration) || DEFAULT_DURATION])]
    .sort((a, b) => a - b)
    .map((m) => ({ value: m, label: `${m} min` }));

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchDoctors());
    dispatch(fetchProcedures());
  }, [dispatch]);

  useEffect(() => {
    setShowPatientModal(false);
    setEditing(false);
    if (editRecord) {
      setForm(fromRecord(editRecord));
    } else {
      const today = new Date().toISOString().split("T")[0];
      setForm({ ...EMPTY, appointmentDate: initialDate || today, fromTime: initialTime || "" });
    }
  }, [editRecord, show, initialDate, initialTime]);

  // Booking for a walk-in who isn't on file yet: register them without losing the half-filled
  // booking, then preselect them so the receptionist can carry straight on.
  const handlePatientSaved = async (saved) => {
    let created = saved;
    try {
      const list = await dispatch(fetchPatients()).unwrap();
      if (!created?.id) {
        // /save can echo back a record without the new id — fall back to matching the
        // refreshed list on the name/mobile pair the form already de-duplicates on.
        const name = (saved?.patientName || "").trim().toLowerCase();
        const mobile = (saved?.mobileNumber || "").trim();
        created =
          list.find(
            (p) => (p.mobileNumber || "").trim() === mobile && (p.patientName || "").trim().toLowerCase() === name
          ) || created;
      }
    } catch {
      // Refresh failed — fall back to whatever the save returned.
    }
    if (created?.id) {
      setForm((f) => ({ ...f, patientId: created.id, patientName: created.patientName || "" }));
    } else {
      Swal.fire("Patient Saved", "The patient was saved but could not be preselected. Please search for them in the list.", "info");
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toTime = addMinutes(form.fromTime, Number(form.duration) || DEFAULT_DURATION);

  const promptWhatsApp = (booked, patientMobile, rescheduled = false) => {
    if (!patientMobile) return;
    const dateStr = fmtDate(booked.appointmentDate || form.appointmentDate);
    const timeStr = fmt12(hhmm(booked.fromTime) || form.fromTime);
    const docName = booked.doctorName || doctors.find((d) => sameId(d.doctorID || d.id, booked.doctorId || form.doctorId))?.doctorName || "";
    const intro = rescheduled ? "Your dental appointment has been rescheduled." : "Your dental appointment has been confirmed.";
    const message = `Hello! ${intro}\n\n📅 Date: ${dateStr}\n⏰ Time: ${timeStr}${docName ? `\n👨‍⚕️ Doctor: ${withDrTitle(docName)}` : ""}\n\nPlease arrive 5 minutes early. Thank you! 🦷`;
    const url = buildWhatsAppURL(patientMobile, message);
    if (!url) return;
    Swal.fire({
      title: rescheduled ? "Appointment Updated!" : "Appointment Booked!",
      text: `Send a WhatsApp ${rescheduled ? "update" : "confirmation"} to the patient?`,
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "Send WhatsApp",
      cancelButtonText: "Skip",
      confirmButtonColor: "#25d366",
    }).then((result) => {
      if (result.isConfirmed) window.open(url, "_blank");
    });
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await dispatch(updateAppointmentThunk({ id: editId, data: form })).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Status updated", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      const msg = getApiErrorMessage(err, "Failed to update status.");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // The API rejects a booking without a procedure ("Select a procedure/service."), so check it here.
    if (!form.patientId || !form.appointmentDate || !form.fromTime || !form.doctorId || !form.procedureId) {
      Swal.fire("Required", "Patient, Doctor, Procedure, Date and From Time are required.", "warning");
      return;
    }
    if (minutesBetween(form.fromTime, toTime) <= 0) {
      Swal.fire("Invalid Time", "The appointment can't run past midnight.", "warning");
      return;
    }
    const data = { ...form, toTime };
    const patientOpt = patientOptions.find((o) => sameId(o.value, form.patientId));
    const mobile = patientOpt?.mobile || editRecord?.patientMobile || "";
    setSaving(true);
    try {
      if (isEdit) {
        const before = fromRecord(editRecord);
        const result = await dispatch(updateAppointmentDetailsThunk({ id: editId, data })).unwrap();
        onClose();
        const moved = before.appointmentDate !== form.appointmentDate || before.fromTime !== form.fromTime
          || !sameId(before.doctorId, form.doctorId);
        if (moved) {
          promptWhatsApp(result || data, mobile, true);
        } else {
          Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Appointment updated", showConfirmButton: false, timer: 2000 });
        }
      } else {
        const result = await dispatch(createAppointmentThunk(data)).unwrap();
        onClose();
        // The booking must keep the chosen length. If the server stored another end time,
        // say so instead of letting the calendar quietly show a longer appointment.
        const savedTo = hhmm(result?.toTime);
        if (savedTo && savedTo !== toTime) {
          Swal.fire(
            "Duration Changed by Server",
            `You booked ${fmt12(form.fromTime)}–${fmt12(toTime)} (${form.duration} min), but the server saved it until ${fmt12(savedTo)}. Please report this to support.`,
            "warning"
          );
        } else {
          promptWhatsApp(result || data, mobile);
        }
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, isEdit ? "Failed to update appointment." : "Failed to book appointment.");
      Swal.fire(isEdit ? "Update Failed" : "Booking Failed", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setForm(fromRecord(editRecord));
    setEditing(true);
  };

  const cancelEditing = () => {
    setForm(fromRecord(editRecord));
    setEditing(false);
  };

  const handleCreateInvoice = () => {
    const rec = editRecord || {};
    const appointmentData = {
      appointmentId: rec.appointmentID || rec.id,
      patientId: rec.patientID || rec.patientId,
      patientName: rec.patientName,
      doctorId: rec.doctorID || rec.doctorId,
      procedureId: rec.procedureID || rec.procedureId,
      procedureName: rec.procedureName,
    };
    onClose();
    navigate(
      `/invoices/new?appointmentId=${appointmentData.appointmentId}`,
      { state: { appointment: appointmentData } }
    );
  };

  const selectedDoc = doctorOptions.find((o) => sameId(o.value, form.doctorId));

  if (!show) return null;

  // Read-only field helpers
  const readVal = (label, value) => (
    <div className="col-lg-6 mb-2">
      <div className="text-muted small">{label}</div>
      <div className="fw-medium">{value || "—"}</div>
    </div>
  );

  const showDetails = isEdit && !editing;
  const title = !isEdit ? "Book Appointment" : editing ? "Edit Appointment" : "Appointment Details";

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{title}</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                {showDetails && canEditDetails && (
                  <button className="btn btn-sm btn-outline-primary" onClick={startEditing} type="button">
                    Edit Appointment
                  </button>
                )}
                {showDetails && (
                  <button className="btn btn-sm btn-success" onClick={handleCreateInvoice} type="button">
                    + Create Invoice
                  </button>
                )}
                <button type="button" className="close" onClick={onClose}><span>×</span></button>
              </div>
            </div>

            <div className="modal-body custom-modal-body">

              {showDetails ? (
                <form onSubmit={handleStatusUpdate}>
                  {/* Header info strip */}
                  <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    {editRecord?.tokenNumber && (
                      <span className="badge bg-dark fs-6">Token #{editRecord.tokenNumber}</span>
                    )}
                    {editRecord?.appointmentID && (
                      <span className="text-muted small">Appt ID: {editRecord.appointmentID}</span>
                    )}
                    <span className={`badge bg-${STATUS_BADGE[editRecord?.status] || "primary"} ms-auto`}>
                      {editRecord?.status === "NoShow" ? "No Show" : (editRecord?.status || "Booked")}
                    </span>
                  </div>

                  {/* Read-only appointment details */}
                  <div className="row border rounded p-2 mb-3 mx-0" style={{ background: "#f9fafb" }}>
                    {readVal("Patient", editRecord?.patientName)}
                    {readVal("Doctor", editRecord?.doctorName ? withDrTitle(editRecord.doctorName) : null)}
                    {readVal("Procedure", editRecord?.procedureName)}
                    {readVal("Date", editRecord?.appointmentDate ? fmtDate(editRecord.appointmentDate) : null)}
                    {readVal("From", fmt12(hhmm(editRecord?.fromTime)))}
                    {readVal("To", fmt12(hhmm(editRecord?.toTime)))}
                    {editRecord?.remarks && (
                      <div className="col-12 mt-1">
                        <div className="text-muted small">Remarks</div>
                        <div className="fw-medium">{editRecord.remarks}</div>
                      </div>
                    )}
                  </div>

                  {/* Editable section */}
                  <div className="row">
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Update Status</label>
                      <Select
                        classNamePrefix="react-select"
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.find((o) => o.value === form.status) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, status: opt?.value ?? "Booked" }))}
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks || ""} onChange={set("remarks")} />
                    </div>
                  </div>

                  <div className="modal-footer-btn">
                    <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Close</button>
                    <button type="submit" className="btn btn-submit" disabled={saving}>
                      {saving ? "Saving..." : "Update Status"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSave}>
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <label className="form-label">Patient <span className="text-danger">*</span></label>
                      <div className="d-flex align-items-start gap-2">
                        <div className="flex-grow-1">
                          <Select
                            classNamePrefix="react-select"
                            options={patientOptions}
                            onChange={(opt) =>
                              setForm((f) => ({ ...f, patientId: opt?.value ?? "", patientName: opt?.patientName ?? "" }))
                            }
                            value={patientOptions.find((o) => sameId(o.value, form.patientId)) || null}
                            placeholder="Search patient by name, mobile or OP No..."
                            isClearable
                            isSearchable
                            menuPosition="fixed"
                            styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary text-nowrap"
                          onClick={() => setShowPatientModal(true)}
                        >
                          Add New
                        </button>
                      </div>
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Doctor <span className="text-danger">*</span></label>
                      <Select
                        classNamePrefix="react-select"
                        options={doctorOptions}
                        value={selectedDoc || null}
                        onChange={(opt) => setForm((f) => ({ ...f, doctorId: opt?.value ?? "" }))}
                        placeholder="Select Doctor"
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {selectedDoc?.availability && (
                        <small className="text-muted mt-1 d-block">
                          Available: {selectedDoc.availability}
                        </small>
                      )}
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Procedure <span className="text-danger">*</span></label>
                      <Select
                        classNamePrefix="react-select"
                        options={procedureOptions}
                        value={procedureOptions.find((o) => sameId(o.value, form.procedureId)) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, procedureId: opt?.value ?? "" }))}
                        placeholder="Select Procedure"
                        isClearable
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">Date <span className="text-danger">*</span></label>
                      <input type="date" className="form-control" value={form.appointmentDate} onChange={set("appointmentDate")} />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">From Time <span className="text-danger">*</span></label>
                      <input type="time" className="form-control" value={form.fromTime} onChange={set("fromTime")} step="600" />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">
                        Duration
                        {toTime && <span className="text-muted ms-2 small">(until {fmt12(toTime)})</span>}
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={durationOptions}
                        value={durationOptions.find((o) => o.value === Number(form.duration)) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, duration: opt?.value ?? DEFAULT_DURATION }))}
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-12 mb-3">
                      <label className="form-label">Remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks} onChange={set("remarks")} />
                    </div>
                  </div>
                  <div className="modal-footer-btn">
                    <button type="button" className="btn btn-cancel me-2" onClick={isEdit ? cancelEditing : onClose}>
                      {isEdit ? "Back" : "Cancel"}
                    </button>
                    <button type="submit" className="btn btn-submit" disabled={saving}>
                      {saving ? "Saving..." : isEdit ? "Save Changes" : "Book Appointment"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <PatientModal
        show={showPatientModal}
        nested
        walkIn
        onClose={() => setShowPatientModal(false)}
        onSaved={handlePatientSaved}
      />
    </>
  );
};

export default AppointmentModal;
